// Shared filter configuration for various pages.
// This file defines which fields are filterable for each resource
// and how they should behave in the generic SharedFilterBar.

export const agentFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'name',
    labelKey: 'name',
    type: 'string',
  },
  {
    key: 'status',
    labelKey: 'filters_field_status',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_started', value: 'started' },
      { labelKey: 'filters_option_stopped', value: 'stopped' },
    ],
  },
  {
    key: 'allow_escalation',
    labelKey: 'filters_field_allow_escalation',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'process_audio',
    labelKey: 'filters_field_process_audio',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'process_document',
    labelKey: 'filters_field_process_document',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'process_image',
    labelKey: 'filters_field_process_image',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'process_video',
    labelKey: 'filters_field_process_video',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: true },
      { labelKey: 'filters_option_disabled', value: false },
    ],
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_date_created',
    type: 'dateRange',
    // Backend expects created_at_start / created_at_end
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_last_updated',
    // Date range in the UI (start & end), mapped directly to
    // updated_at_start / updated_at_end for the backend.
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Filters for knowledge bases list (/knowledge-bases)
export const knowledgeBaseFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'name',
    labelKey: 'name',
    type: 'string',
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_last_updated',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Filters for transactions list (/transactions, /transactions/me)
export const transactionFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'user_id',
    labelKey: 'user',
    type: 'enum',
    options: [], // populated dynamically
  },
  {
    key: 'transaction_type',
    labelKey: 'filters_field_transaction_type',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_transaction_type_payment', value: 'payment' },
      { labelKey: 'filters_option_transaction_type_message', value: 'message' },
      { labelKey: 'filters_option_transaction_type_admin', value: 'admin' },
      { labelKey: 'filters_option_transaction_type_system', value: 'system' },
    ],
  },
  {
    key: 'transaction_status',
    labelKey: 'filters_field_status',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_pending', value: 'pending' },
      { labelKey: 'filters_option_completed', value: 'completed' },
      { labelKey: 'filters_option_cancelled', value: 'cancelled' },
    ],
  },
  {
    key: 'is_disabled',
    labelKey: 'filters_field_disabled',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: false },
      { labelKey: 'filters_option_disabled', value: true },
    ],
  },
  {
    key: 'coins_amount_min',
    labelKey: 'filters_field_min_coins',
    type: 'number',
    min: 0,
  },
  {
    key: 'coins_amount_max',
    labelKey: 'filters_field_max_coins',
    type: 'number',
    min: 0,
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
  {
    key: 'disabled_at',
    labelKey: 'filters_field_disabled_at',
    type: 'dateRange',
    startKey: 'disabled_at_start',
    endKey: 'disabled_at_end',
  },
];

// Filters for payments list (/payments) - used by Billing -> Payment History
// Backend accepts these keys (see validate_get_all_payments):
//  - payment_status, payment_type, currency
//  - total_min/max, coins_amount_min/max
//  - created_at_start/end, paid_at_start/end (and other date ranges)
//  - is_refunded, is_disputed, has_error (booleans)
export const paymentFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
    sortKey: 'id',
  },
  {
    key: 'payment_status',
    labelKey: 'filters_field_payment_status',
    type: 'enumMulti',
    sortKey: 'payment_status',
    options: [
      { labelKey: 'filters_option_payment_status_created', value: 'created' },
      { labelKey: 'filters_option_payment_status_processing', value: 'processing' },
      { labelKey: 'filters_option_payment_status_paid', value: 'paid' },
      { labelKey: 'filters_option_payment_status_failed', value: 'failed' },
      { labelKey: 'filters_option_payment_status_refunded', value: 'refunded' },
      { labelKey: 'filters_option_payment_status_partially_refunded', value: 'partially_refunded' },
      { labelKey: 'filters_option_payment_status_disputed', value: 'disputed' },
    ],
  },
  {
    key: 'payment_type',
    labelKey: 'filters_field_payment_type',
    type: 'enumMulti',
    sortKey: 'payment_type',
    options: [
      { labelKey: 'filters_option_payment_type_checkout', value: 'checkout' },
      { labelKey: 'filters_option_payment_type_auto_reload', value: 'auto_reload' },
    ],
  },
  {
    key: 'currency',
    labelKey: 'filters_field_currency',
    type: 'enumMulti',
    sortKey: 'currency',
    options: [
      { labelKey: 'filters_option_currency_usd', value: 'USD' },
      { labelKey: 'filters_option_currency_eur', value: 'EUR' },
      { labelKey: 'filters_option_currency_gbp', value: 'GBP' },
    ],
  },
  {
    key: 'total_min',
    labelKey: 'filters_field_min_total',
    type: 'number',
    min: 0,
    sortKey: 'total',
  },
  {
    key: 'total_max',
    labelKey: 'filters_field_max_total',
    type: 'number',
    min: 0,
    sortKey: 'total',
  },
  {
    key: 'coins_amount_min',
    labelKey: 'filters_field_min_coins',
    type: 'number',
    min: 0,
    sortKey: 'coins_amount',
  },
  {
    key: 'coins_amount_max',
    labelKey: 'filters_field_max_coins',
    type: 'number',
    min: 0,
    sortKey: 'coins_amount',
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    sortKey: 'created_at',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'paid_at',
    labelKey: 'filters_field_paid_at',
    type: 'dateRange',
    sortKey: 'paid_at',
    startKey: 'paid_at_start',
    endKey: 'paid_at_end',
  },
  {
    key: 'is_refunded',
    labelKey: 'filters_field_refunded',
    type: 'enumMulti',
    sortKey: 'is_refunded',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'is_disputed',
    labelKey: 'filters_field_disputed',
    type: 'enumMulti',
    sortKey: 'is_disputed',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'has_error',
    labelKey: 'filters_field_has_error',
    type: 'enumMulti',
    sortKey: 'has_error',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
];

// Filters for users list (/users)
export const userFilterFields = [
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
  {
    key: 'email',
    labelKey: 'email',
    type: 'string',
  },
  {
    key: 'is_admin',
    labelKey: 'filters_field_role',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_role_admin', value: true },
      { labelKey: 'filters_option_role_user', value: false },
    ],
  },
  {
    key: 'is_verified',
    labelKey: 'filters_field_verified',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_verified', value: true },
      { labelKey: 'filters_option_unverified', value: false },
    ],
  },
  {
    key: 'is_disabled',
    labelKey: 'filters_field_disabled',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_enabled', value: false },
      { labelKey: 'filters_option_disabled', value: true },
    ],
  },
  {
    key: 'gender',
    labelKey: 'filters_field_gender',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_gender_male', value: 'male' },
      { labelKey: 'filters_option_gender_female', value: 'female' },
    ],
  },
  {
    key: 'locale',
    labelKey: 'language',
    type: 'enumMulti',
    options: [
      { label: 'English', value: 'en' },
      { label: 'Français', value: 'fr' },
      { label: 'Español', value: 'es' },
      { label: 'Deutsch', value: 'de' },
      { label: '中文', value: 'zh' },
      { label: 'العربية', value: 'ar' },
    ],
  },
  {
    key: 'timezone_name',
    labelKey: 'timezone',
    type: 'string',
  },
  {
    key: 'discount_percentage',
    labelKey: 'discount_percentage',
    type: 'number',
    min: 0,
    max: 100,
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
  {
    key: 'disabled_at',
    labelKey: 'filters_field_disabled_at',
    type: 'dateRange',
    startKey: 'disabled_at_start',
    endKey: 'disabled_at_end',
  },
  {
    key: 'discount_expiry',
    labelKey: 'filters_field_discount_expiry',
    type: 'dateRange',
    startKey: 'discount_expiry_start',
    endKey: 'discount_expiry_end',
  },
  {
    key: 'welcome_credit_at',
    labelKey: 'welcome_credit_at',
    type: 'dateRange',
    startKey: 'welcome_credit_at_start',
    endKey: 'welcome_credit_at_end',
  },
  {
    key: 'discount_added_at',
    labelKey: 'discount_added_at',
    type: 'dateRange',
    startKey: 'discount_added_at_start',
    endKey: 'discount_added_at_end',
  },
];

// Filters for conversations (advanced filters in Messages)
export const conversationFilterFields = [
  {
    key: 'status',
    labelKey: 'filters_field_status',
    type: 'enumMulti',
    options: [
      // Backend accepts only ConversationStatus values: 'active' | 'inactive'
      // We keep user-friendly labels but map them to backend values.
      { labelKey: 'filters_option_open', value: 'active' },
      { labelKey: 'filters_option_closed', value: 'inactive' },
    ],
  },
  {
    key: 'contact_type',
    labelKey: 'filters_field_contact_type',
    // Backend currently expects a single contact_type value
    // (ConversationService compares ContactModel.contact_type == value),
    // so we use a single-select enum instead of enumMulti.
    type: 'enum',
    options: [
      { labelKey: 'filters_option_contact_type_admin', value: 'admin' },
      { labelKey: 'filters_option_contact_type_customer', value: 'customer' },
    ],
  },
  {
    key: 'is_ignored',
    labelKey: 'filters_field_ignored',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'is_snoozed',
    labelKey: 'filters_field_snoozed',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_yes', value: true },
      { labelKey: 'filters_option_no', value: false },
    ],
  },
  {
    key: 'is_seen_by_user',
    labelKey: 'filters_field_seen_by_user',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_seen', value: true },
      { labelKey: 'filters_option_unseen', value: false },
    ],
  },
  {
    key: 'is_virtual',
    labelKey: 'filters_field_virtual',
    type: 'enumMulti',
    options: [
      { labelKey: 'filters_option_virtual', value: true },
      { labelKey: 'filters_option_real', value: false },
    ],
  },
  {
    key: 'updated_time',
    labelKey: 'filters_field_last_activity',
    type: 'dateRange',
    startKey: 'updated_time_start',
    endKey: 'updated_time_end',
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'updated_at',
    labelKey: 'filters_field_updated_at',
    type: 'dateRange',
    startKey: 'updated_at_start',
    endKey: 'updated_at_end',
  },
];

// Filters for data exports list (/data-exports) - used by Settings -> Data Exports
// Backend accepts:
//  - status (string or list)
//  - created_at_start/end, updated_at_start/end, started_at_start/end, completed_at_start/end
//  - id (encoded id string or list)
export const dataExportFilterFields = [
  {
    key: 'status',
    labelKey: 'status',
    type: 'enumMulti',
    options: [
      { labelKey: 'pending', value: 'pending' },
      { labelKey: 'processing', value: 'processing' },
      { labelKey: 'completed', value: 'completed' },
      { labelKey: 'failed', value: 'failed' },
    ],
  },
  {
    key: 'created_at',
    labelKey: 'filters_field_created_at',
    type: 'dateRange',
    startKey: 'created_at_start',
    endKey: 'created_at_end',
  },
  {
    key: 'completed_at',
    labelKey: 'completion_date',
    type: 'dateRange',
    startKey: 'completed_at_start',
    endKey: 'completed_at_end',
  },
  {
    key: 'id',
    labelKey: 'id',
    type: 'string',
  },
];
